import * as THREE from "three";
import { LIGHT, T, TILES, asteroidPulls, isMachineAnchor, isShaped, openToOutside, revealAfterDig, tileShape, visibleTiles, type AsteroidBody, type AsteroidDef, type Vec2 } from "@dyson/shared";

const isTurretAnchor = (t: number) => isMachineAnchor(t, "turret");
const isLantern = (t: number) => TILES[t]?.light === true;
import { GridMesh } from "./GridMesh";
import { lanternGlow, type Lantern } from "./lanterns";
import { TreeViews } from "./Trees";
import { AsteroidLife } from "./AsteroidLife";
import { TurretBarrels } from "./TurretBarrels";
import { facing } from "./tileFacing";

/** What each kind of rock is mostly made of, as unexplored rock shows (deep stone for the rest). */
const HOSTS: Partial<Record<AsteroidDef["kind"], number>> = { coal: T.COAL, iron: T.ORE, ice: T.ICE };

/** Chunk size in tiles. Digging rebuilds only the chunks whose tiles or visibility changed. */
const CHUNK = 64;

/**
 * An asteroid drawn as a grid of chunk meshes, with unexplored rock hidden
 * (see visibleTiles). Light (the suns', and any lanterns built here) is baked
 * into the vertices: most asteroids never move, and a drifting one never
 * goes far enough for its light to change much.
 */
export class AsteroidView {
  readonly group = new THREE.Group();
  private readonly chunks: GridMesh[] = [];
  /** Gun barrels on turrets built here. */
  private readonly barrels = new TurretBarrels();
  /** Its trees, as sprites. */
  private readonly trees: TreeViews;
  /** The small life round its edge, the rock behind it, its haze and motes (see AsteroidLife). */
  private readonly life: AsteroidLife;
  private readonly cols: number;
  private readonly rows: number;
  /** Open tiles joined to the outside, and the tiles that can be seen (them and their neighbours). See visibleTiles. */
  private readonly reached: Uint8Array;
  private readonly visible: Uint8Array;
  /** Lanterns built here, by tile index. */
  private readonly lanterns = new Set<number>();

  constructor(
    private readonly a: AsteroidBody,
    private readonly light: (x: number, y: number) => number,
  ) {
    this.group.position.set(a.def.ox, a.def.oy, 0);
    this.group.name = a.def.id;
    this.cols = Math.ceil(a.grid.w / CHUNK);
    this.rows = Math.ceil(a.grid.h / CHUNK);
    for (let i = 0; i < this.cols * this.rows; i++) {
      const m = new GridMesh();
      this.chunks.push(m);
      this.group.add(m.mesh);
    }
    this.reached = openToOutside(a.grid);
    this.visible = visibleTiles(a.grid);
    a.grid.tiles.forEach((t, i) => isLantern(t) && this.lanterns.add(i));
    for (let i = 0; i < this.chunks.length; i++) this.rebuildChunk(i);
    this.group.add(this.barrels.group);
    this.trees = new TreeViews(a, light);
    this.group.add(this.trees.group);
    this.life = new AsteroidLife(a, light, this.reached);
    this.group.add(this.life.group);
    a.grid.tiles.forEach((t, i) => isTurretAnchor(t) && this.barrels.add(a.grid, i));
  }

  /**
   * Tiles of the asteroid changed (one dug, or a whole tree): redraw just
   * what they touch, each chunk once. A dug tile that opens onto the outside
   * opens up whatever pocket lies behind it, found by walking out from it (not
   * by re-flooding the whole asteroid, which is far too slow to do on every
   * dig). Once seen, rock stays seen.
   */
  update(indices: number | readonly number[]): void {
    const dirty = new Set<number>();
    for (const index of typeof indices === "number" ? [indices] : indices) this.touchTile(index, dirty);
    for (const c of dirty) this.rebuildChunk(c);
    this.trees.update(indices);
    this.life.update(indices);
  }

  /** What tile `index` changing means: the chunks to redraw go in `dirty`; turrets are put up or taken down. */
  private touchTile(index: number, dirty: Set<number>): void {
    const g = this.a.grid;
    const { w, h } = g;
    const touch = (x: number, y: number) => {
      if (x >= 0 && y >= 0 && x < w && y < h) dirty.add(Math.floor(y / CHUNK) * this.cols + Math.floor(x / CHUNK));
    };
    const x0 = index % w;
    const y0 = (index - x0) / w;
    // The tile and two all round it: grass beside a dug tile may turn to face the new opening, and the smooth ground reshapes (see groundShape) that far.
    for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) touch(x0 + dx, y0 + dy);

    // A lantern put down or taken up relights everything in its reach.
    const lantern = isLantern(g.tiles[index]!);
    if (lantern !== this.lanterns.has(index)) {
      if (lantern) this.lanterns.add(index);
      else this.lanterns.delete(index);
      // As far as the furthest-reaching lamp (it may be one just taken up).
      const r = Math.ceil(LIGHT.radius * 2);
      const [cx0, cx1] = [Math.max(0, x0 - r), Math.min(w - 1, x0 + r)].map((x) => Math.floor(x / CHUNK)) as [number, number];
      const [cy0, cy1] = [Math.max(0, y0 - r), Math.min(h - 1, y0 + r)].map((y) => Math.floor(y / CHUNK)) as [number, number];
      for (let cy = cy0; cy <= cy1; cy++) for (let cx = cx0; cx <= cx1; cx++) dirty.add(cy * this.cols + cx);
    }

    // Newly seen tiles, and the ground around them (which shapes itself to what can be seen).
    revealAfterDig(g, this.reached, this.visible, index, (j) => {
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) touch((j % w) + dx, Math.floor(j / w) + dy);
    });
    // A turret goes down (or comes out) anchor first.
    if (isTurretAnchor(g.tiles[index]!)) this.barrels.add(g, index);
    else this.barrels.remove(index);
  }

  /**
   * How bright the suns are on this rock now, against usual (1): an outage
   * dims the light baked into it, its trees and its little life, but not its
   * lanterns (see NIGHT).
   */
  setSun(k: number): void {
    for (const m of this.chunks) m.setSun(k);
    this.trees.setSun(k);
    this.life.setSun(k);
  }

  /** A drifting rock: draw it where it is now. */
  place(rockAt: (def: AsteroidDef) => Vec2): void {
    if (!this.a.def.drift) return;
    const at = rockAt(this.a.def);
    this.group.position.set(at.x, at.y, 0);
  }

  /**
   * What the rock is made of at a tile, as unexplored rock shows where
   * nothing round it says: a nugget's coal or ore, an ice rock's ice, else
   * its layers as generation lays them (see bodyPass): a dense core of deep
   * stone, ordinary stone round it.
   */
  private readonly host = (x: number, y: number): number => {
    const { def, grid } = this.a;
    const made = HOSTS[def.kind];
    if (made !== undefined) return made;
    const core = def.radius * (def.kind === "metal" ? 0.4 : 0.5);
    return Math.hypot(x + 0.5 - grid.w / 2, y + 0.5 - grid.h / 2) < def.radius * 1.1 - core ? T.DEEP_ROCK : T.ROCK;
  };

  /** Point the turrets' barrels where they're aiming. */
  aimTurrets(): void {
    if (this.barrels.size) this.barrels.aim(this.a.turrets);
  }

  private rebuildChunk(c: number): void {
    const cx = c % this.cols;
    const cy = (c - cx) / this.cols;
    const g = this.a.grid;
    const region = { x0: cx * CHUNK, y0: cy * CHUNK, x1: Math.min(g.w, (cx + 1) * CHUNK), y1: Math.min(g.h, (cy + 1) * CHUNK) };
    // Only the lanterns within reach of this chunk light it.
    const r = LIGHT.radius;
    const near: Lantern[] = [];
    for (const i of this.lanterns) {
      const x = i % g.w;
      const y = (i - x) / g.w;
      if (x >= region.x0 - r && x < region.x1 + r && y >= region.y0 - r && y < region.y1 + r) near.push({ x, y });
    }
    const sun = this.light;
    const pulls = asteroidPulls(this.a.def);
    this.chunks[c]!.rebuild(g, {
      light: sun,
      lamp: near.length ? (x, y) => LIGHT.strength * lanternGlow(near, x, y) : undefined,
      visible: this.visible,
      // Unexplored, it looks like what it's made of (see unexplored in GridMesh).
      host: this.host,
      fade: true,
      orient: (x, y, t) => facing(g, x, y, t, pulls),
      // Ground smooths itself to what can be seen (see groundShape): unexplored rock counts as ground. Shaped blocks have their own outline (see shapes.ts).
      shape: (x, y) => tileShape(g, x, y, (nx, ny) => this.visible[ny * g.w + nx] !== 1 || isShaped(g.tiles[ny * g.w + nx]!)),
      region,
    });
  }

  dispose(): void {
    for (const m of this.chunks) m.dispose();
    this.trees.dispose();
    this.life.dispose();
  }
}
